/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useMemo, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'

// Components
import {
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    Box,
    Button,
    Container,
    Flex,
    Heading,
    List,
    ListItem,
    SimpleGrid,
    Stack,
    Text,
    useMultiStyleConfig,

    // Hooks
    useTheme,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {MinusIcon, AddIcon} from '@chakra-ui/icons'
// import Link from '@salesforce/retail-react-app/app/components/link'

// Project Components
import LinksList from './links-list'
// Others
import {categoryUrlBuilder} from '../../utills/url'
import {parseCategoryColorConfig} from '../../utills/parseCategoryColorConfig'
import {MENU_DEFAULT_TEXT_COLOR, MENU_DEFAULT_HOVER_COLOR} from '../../constants'
import {ChevronRightIcon, ChevronLeftIcon} from '../icons'
import {pxToRem} from '../../utills/pxToRem'
import {fonts} from '../../theme/foundations/fonts'

const ListMenuContent = ({
    item,
    itemsKey,
    onClose,
    setfeatureImage,
    setSelectedLevelOneMenu,
    setLinksForLevelThreeMenu,
    setSelectedLevelTwoMenu,
    selectedLevelOneMenu,
    selectedLevelTwoMenu,
    setLinksForLevelTwoMenu,
    setIsLevelThreeMenuOpen,
    setIsLevelTwoMenuOpen,
    isLevelTwoMenuOpen,
    isLevelThreeMenuOpen,
    isMobileMenu = false,
    setLinksForContentComp,
    setFeatureImageMobileLevelTwo,
    setFeatureImageMobileLevelThree,
    setFeatureImageMobileLevelFour,
    setMenuToggle
}) => {
    const theme = useTheme()
    const {baseStyle} = theme.components.ListMenu
    const {locale} = useIntl()
    const intl = useIntl()
    const items = item?.[itemsKey] || []
    const [expandedIndex, setExpandedIndex] = useState(-1)

    const shiftHeldRef = useRef(false)
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Shift') shiftHeldRef.current = true
        }
        const onKeyUp = (e) => {
            if (e.key === 'Shift') shiftHeldRef.current = false
        }
        document.addEventListener('keydown', onKeyDown)
        document.addEventListener('keyup', onKeyUp)
        return () => {
            document.removeEventListener('keydown', onKeyDown)
            document.removeEventListener('keyup', onKeyUp)
        }
    }, [])

    const accordionIndexMap = useMemo(() => {
        let accIdx = 0
        return items.map((cat) => {
            const subs = cat[itemsKey] || []
            return subs.length > 0 ? accIdx++ : -1
        })
    }, [items, itemsKey])

    const variant = 'vertical'

    const styles = useMultiStyleConfig('LinksList', {variant})

    const handleCloseMainContent = () => {
        setLinksForContentComp([])
    }

    const updateGTMdataLayer = (categoryName) => {
        if (window?.dataLayer) {
            window.dataLayer.push({
                event: 'mainNavClick',
                valueL1: categoryName,
                valueL2: `view all ${categoryName}`
            })
        }
    }
    const parsedItemColors = parseCategoryColorConfig(item?.c_categoryTextColor)
    const categoryTextColor = parsedItemColors?.textColor ?? MENU_DEFAULT_TEXT_COLOR
    const categoryHoverColor = parsedItemColors?.hoverColor ?? MENU_DEFAULT_HOVER_COLOR

    const viewAllHref = categoryUrlBuilder(item, locale)
    const parentCategoryName = item.name
    return (
        <>
            <Container
                as={Stack}
                {...baseStyle.popoverContainer}
                {...baseStyle.overflow}
                position={'relative'}
                {...(isMobileMenu
                    ? {
                          paddingLeft: 0,
                          paddingRight: 0,
                          paddingTop: 0,
                          height: 'auto',
                          overflowX: 'unset'
                      }
                    : {})}
            >
                <Flex
                    direction="column"
                    flex={'1'}
                    gap={pxToRem(30)}
                    borderRight={
                        !isMobileMenu
                            ? `${pxToRem(1)} solid ${MENU_DEFAULT_TEXT_COLOR}`
                            : `0 solid ${MENU_DEFAULT_TEXT_COLOR}`
                    }
                >
                    {isMobileMenu && (
                        <Box
                            as="button"
                            type="button"
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{py: 0}}
                            cursor="pointer"
                            onClick={handleCloseMainContent}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    handleCloseMainContent()
                                }
                            }}
                            width="100%"
                            backgroundColor="transparent"
                            border="none"
                            padding={0}
                            textAlign="left"
                            aria-label={intl.formatMessage(
                                {
                                    id: 'mobile_menu.button.back',
                                    defaultMessage: 'Back to {category}'
                                },
                                {category: selectedLevelOneMenu?.name || 'menu'}
                            )}
                            _focus={{
                                outline: `${pxToRem(2)} solid ${MENU_DEFAULT_HOVER_COLOR}`,
                                outlineOffset: pxToRem(2)
                            }}
                            _focusVisible={{
                                outline: `${pxToRem(2)} solid ${MENU_DEFAULT_HOVER_COLOR}`,
                                outlineOffset: pxToRem(2)
                            }}
                        >
                            <ChevronLeftIcon
                                boxSize={pxToRem(14)}
                                color={MENU_DEFAULT_HOVER_COLOR}
                                sx={{
                                    '& use': {
                                        transform: 'scale(1.5)',
                                        transformOrigin: 'center',
                                        stroke: 'currentColor',
                                        strokeWidth: '1'
                                    }
                                }}
                            />
                            <Box display="flex" alignItems="center" gap={pxToRem(8)}>
                                {item?.c_categoryIcon && (
                                    <Box
                                        as="span"
                                        display="inline-flex"
                                        alignItems="center"
                                        dangerouslySetInnerHTML={{
                                            __html: item.c_categoryIcon
                                        }}
                                    />
                                )}
                                <Heading
                                    fontFamily={fonts.primary}
                                    fontSize={pxToRem(12)}
                                    fontWeight="600"
                                    letterSpacing={pxToRem(1)}
                                    textTransform="uppercase"
                                    color={categoryHoverColor}
                                    margin={0}
                                    textDecoration="underline"
                                >
                                    {item.name}
                                </Heading>
                            </Box>
                        </Box>
                    )}

                    {/* View All link for the parent category */}
                    <Link
                        href={item?.c_alternativeUrl || viewAllHref}
                        onClick={() => {
                            updateGTMdataLayer(item.name)
                            if (setMenuToggle) setMenuToggle(false)
                            if (onClose) onClose()
                        }}
                        display="flex"
                        alignItems="center"
                        fontFamily={fonts.primary}
                        fontSize={pxToRem(14)}
                        fontWeight="500"
                        letterSpacing={pxToRem(1.12)}
                        lineHeight="normal"
                        fontStyle="normal"
                        color={MENU_DEFAULT_TEXT_COLOR}
                        textDecoration="none"
                        _hover={{
                            textDecoration: 'underline',
                            color: categoryHoverColor
                        }}
                        _focus={{
                            outline: `${pxToRem(2)} solid ${categoryHoverColor}`,
                            outlineOffset: pxToRem(2),
                            textDecoration: 'underline',
                            color: categoryHoverColor
                        }}
                        _focusVisible={{
                            outline: `${pxToRem(2)} solid ${categoryHoverColor}`,
                            outlineOffset: pxToRem(2),
                            textDecoration: 'underline',
                            color: categoryHoverColor
                        }}
                    >
                        View All {item.name}
                    </Link>

                    {/* level category menu items */}
                    <Accordion
                        allowToggle
                        index={expandedIndex}
                        onChange={(index) => setExpandedIndex(index)}
                        display="flex"
                        flexDirection="column"
                        gap={pxToRem(30)}
                    >
                        {items.map((category, itemIndex) => {
                            const {
                                id,
                                name,
                                c_categoryTextColor: catTextColor,
                                c_categoryIcon: catIcon,
                                c_categoryIconPosition: catIconPosition
                            } = category
                            const subcategories = category[itemsKey] || []
                            const hasSubcategories = subcategories.length > 0
                            const categoryHref = categoryUrlBuilder(category, locale)

                            const parsedColors = parseCategoryColorConfig(catTextColor)
                            const textColor = parsedColors?.textColor ?? MENU_DEFAULT_TEXT_COLOR
                            const hoverColor = parsedColors?.hoverColor ?? MENU_DEFAULT_HOVER_COLOR

                            const handleCategoryLinkClick = (categoryName) => {
                                if (window?.dataLayer) {
                                    window.dataLayer.push({
                                        event: 'mainNavClick',
                                        valueL1: parentCategoryName,
                                        valueL2: categoryName
                                    })
                                }
                                if (setMenuToggle) setMenuToggle(false)
                                if (onClose) onClose()
                            }

                            if (hasSubcategories) {
                                return (
                                    <AccordionItem key={id} border="none">
                                        {({isExpanded}) => (
                                            <>
                                                <AccordionButton
                                                    onFocus={(e) => {
                                                        if (e.currentTarget !== e.target) return
                                                        const accIdx = accordionIndexMap[itemIndex]
                                                        const button = e.currentTarget
                                                        const panel = button.nextElementSibling
                                                        const focusCameFromPanel =
                                                            panel &&
                                                            e.relatedTarget &&
                                                            panel.contains(e.relatedTarget)
                                                        setExpandedIndex(accIdx)
                                                        if (
                                                            shiftHeldRef.current &&
                                                            !focusCameFromPanel
                                                        ) {
                                                            setTimeout(() => {
                                                                const links =
                                                                    panel?.querySelectorAll(
                                                                        'a[href]:not([disabled])'
                                                                    )
                                                                if (links?.length) {
                                                                    links[links.length - 1].focus()
                                                                }
                                                            }, 0)
                                                        }
                                                    }}
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    padding={0}
                                                    paddingTop={pxToRem(5)}
                                                    paddingBottom={pxToRem(5)}
                                                    _hover={{
                                                        backgroundColor: 'transparent'
                                                    }}
                                                    _focus={{
                                                        outline: `${pxToRem(
                                                            2
                                                        )} solid ${hoverColor}`,
                                                        outlineOffset: pxToRem(2),
                                                        backgroundColor: 'transparent'
                                                    }}
                                                    _focusVisible={{
                                                        outline: `${pxToRem(
                                                            2
                                                        )} solid ${hoverColor}`,
                                                        outlineOffset: pxToRem(2),
                                                        backgroundColor: 'transparent'
                                                    }}
                                                >
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        flex="1"
                                                    >
                                                        {catIcon &&
                                                            (catIconPosition || 'left') ===
                                                                'left' && (
                                                                <Box
                                                                    as="span"
                                                                    aria-hidden="true"
                                                                    tabIndex={-1}
                                                                    marginRight={pxToRem(8)}
                                                                    display="inline-flex"
                                                                    alignItems="center"
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: catIcon
                                                                    }}
                                                                />
                                                            )}
                                                        <Text
                                                            fontFamily={fonts.primary}
                                                            fontSize={pxToRem(14)}
                                                            fontWeight="500"
                                                            letterSpacing={pxToRem(1.12)}
                                                            lineHeight="normal"
                                                            fontStyle="normal"
                                                            color={textColor}
                                                            textAlign="left"
                                                            margin={0}
                                                        >
                                                            {name}
                                                        </Text>
                                                        {catIcon &&
                                                            (catIconPosition || 'left') ===
                                                                'right' && (
                                                                <Box
                                                                    as="span"
                                                                    aria-hidden="true"
                                                                    tabIndex={-1}
                                                                    marginLeft={pxToRem(8)}
                                                                    display="inline-flex"
                                                                    alignItems="center"
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: catIcon
                                                                    }}
                                                                />
                                                            )}
                                                    </Box>
                                                    {isExpanded ? (
                                                        <MinusIcon
                                                            boxSize={pxToRem(17)}
                                                            color={textColor}
                                                        />
                                                    ) : (
                                                        <AddIcon
                                                            boxSize={pxToRem(17)}
                                                            color={textColor}
                                                        />
                                                    )}
                                                </AccordionButton>
                                                <AccordionPanel padding={0}>
                                                    <List
                                                        spacing={pxToRem(10)}
                                                        styleType="none"
                                                        margin={0}
                                                        marginTop={pxToRem(15)}
                                                        padding={0}
                                                    >
                                                        <ListItem listStyleType="none">
                                                            <Link
                                                                href={categoryHref}
                                                                onClick={() =>
                                                                    handleCategoryLinkClick(
                                                                        `View All ${name}`
                                                                    )
                                                                }
                                                                display="flex"
                                                                alignItems="center"
                                                                fontFamily={fonts.primary}
                                                                fontSize={pxToRem(14)}
                                                                fontWeight="400"
                                                                letterSpacing={pxToRem(1.12)}
                                                                lineHeight="normal"
                                                                fontStyle="normal"
                                                                color={textColor}
                                                                textDecoration="none"
                                                                margin={0}
                                                                padding={0}
                                                                _hover={{
                                                                    textDecoration: 'underline',
                                                                    color: hoverColor
                                                                }}
                                                                _focus={{
                                                                    outline: `${pxToRem(
                                                                        2
                                                                    )} solid ${hoverColor}`,
                                                                    outlineOffset: pxToRem(2),
                                                                    textDecoration: 'underline',
                                                                    color: hoverColor
                                                                }}
                                                                _focusVisible={{
                                                                    outline: `${pxToRem(
                                                                        2
                                                                    )} solid ${hoverColor}`,
                                                                    outlineOffset: pxToRem(2),
                                                                    textDecoration: 'underline',
                                                                    color: hoverColor
                                                                }}
                                                            >
                                                                View All {name}
                                                            </Link>
                                                        </ListItem>
                                                        {subcategories.map((subcategory) => {
                                                            const l3Parsed =
                                                                parseCategoryColorConfig(
                                                                    subcategory?.c_categoryTextColor
                                                                )
                                                            const l3TextColor =
                                                                l3Parsed?.textColor ??
                                                                MENU_DEFAULT_TEXT_COLOR
                                                            const l3HoverColor =
                                                                l3Parsed?.hoverColor ??
                                                                MENU_DEFAULT_HOVER_COLOR
                                                            return (
                                                                <ListItem
                                                                    key={subcategory.id}
                                                                    listStyleType="none"
                                                                >
                                                                    <Link
                                                                        href={categoryUrlBuilder(
                                                                            subcategory,
                                                                            locale
                                                                        )}
                                                                        onClick={() =>
                                                                            handleCategoryLinkClick(
                                                                                subcategory.name
                                                                            )
                                                                        }
                                                                        display="flex"
                                                                        alignItems="center"
                                                                        fontFamily={fonts.primary}
                                                                        fontSize={pxToRem(14)}
                                                                        fontWeight="400"
                                                                        letterSpacing={pxToRem(
                                                                            1.12
                                                                        )}
                                                                        lineHeight="normal"
                                                                        fontStyle="normal"
                                                                        color={l3TextColor}
                                                                        textDecoration="none"
                                                                        margin={0}
                                                                        padding={0}
                                                                        _hover={{
                                                                            textDecoration:
                                                                                'underline',
                                                                            color: l3HoverColor
                                                                        }}
                                                                        _focus={{
                                                                            outline: `${pxToRem(
                                                                                2
                                                                            )} solid ${l3HoverColor}`,
                                                                            outlineOffset:
                                                                                pxToRem(2),
                                                                            textDecoration:
                                                                                'underline',
                                                                            color: l3HoverColor
                                                                        }}
                                                                        _focusVisible={{
                                                                            outline: `${pxToRem(
                                                                                2
                                                                            )} solid ${l3HoverColor}`,
                                                                            outlineOffset:
                                                                                pxToRem(2),
                                                                            textDecoration:
                                                                                'underline',
                                                                            color: l3HoverColor
                                                                        }}
                                                                    >
                                                                        {subcategory?.c_categoryIcon &&
                                                                            (subcategory?.c_categoryIconPosition ||
                                                                                'left') ===
                                                                                'left' && (
                                                                                <Box
                                                                                    as="span"
                                                                                    aria-hidden="true"
                                                                                    tabIndex={-1}
                                                                                    marginRight={pxToRem(
                                                                                        8
                                                                                    )}
                                                                                    display="inline-flex"
                                                                                    alignItems="center"
                                                                                    dangerouslySetInnerHTML={{
                                                                                        __html: subcategory.c_categoryIcon
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        {subcategory.name}
                                                                        {subcategory?.c_categoryIcon &&
                                                                            (subcategory?.c_categoryIconPosition ||
                                                                                'left') ===
                                                                                'right' && (
                                                                                <Box
                                                                                    as="span"
                                                                                    aria-hidden="true"
                                                                                    tabIndex={-1}
                                                                                    marginLeft={pxToRem(
                                                                                        8
                                                                                    )}
                                                                                    display="inline-flex"
                                                                                    alignItems="center"
                                                                                    dangerouslySetInnerHTML={{
                                                                                        __html: subcategory.c_categoryIcon
                                                                                    }}
                                                                                />
                                                                            )}
                                                                    </Link>
                                                                </ListItem>
                                                            )
                                                        })}
                                                    </List>
                                                </AccordionPanel>
                                            </>
                                        )}
                                    </AccordionItem>
                                )
                            }

                            // No subcategories
                            return (
                                <Box key={id}>
                                    <Link
                                        href={categoryHref}
                                        onClick={() => handleCategoryLinkClick(name)}
                                        display="flex"
                                        alignItems="center"
                                        fontFamily={fonts.primary}
                                        fontSize={pxToRem(14)}
                                        fontWeight="500"
                                        letterSpacing={pxToRem(1.12)}
                                        lineHeight="normal"
                                        fontStyle="normal"
                                        color={textColor}
                                        textDecoration="none"
                                        _hover={{
                                            textDecoration: 'underline',
                                            color: hoverColor
                                        }}
                                        _focus={{
                                            outline: `${pxToRem(2)} solid ${hoverColor}`,
                                            outlineOffset: pxToRem(2),
                                            textDecoration: 'underline',
                                            color: hoverColor
                                        }}
                                        _focusVisible={{
                                            outline: `${pxToRem(2)} solid ${hoverColor}`,
                                            outlineOffset: pxToRem(2),
                                            textDecoration: 'underline',
                                            color: hoverColor
                                        }}
                                    >
                                        {catIcon && (catIconPosition || 'left') === 'left' && (
                                            <Box
                                                as="span"
                                                marginRight={pxToRem(8)}
                                                display="inline-flex"
                                                alignItems="center"
                                                dangerouslySetInnerHTML={{
                                                    __html: catIcon
                                                }}
                                            />
                                        )}
                                        {name}
                                        {catIcon && (catIconPosition || 'left') === 'right' && (
                                            <Box
                                                as="span"
                                                marginLeft={pxToRem(8)}
                                                display="inline-flex"
                                                alignItems="center"
                                                dangerouslySetInnerHTML={{
                                                    __html: catIcon
                                                }}
                                            />
                                        )}
                                    </Link>
                                </Box>
                            )
                        })}
                    </Accordion>
                </Flex>
            </Container>
        </>
    )
}

ListMenuContent.propTypes = {
    item: PropTypes.object,
    itemsKey: PropTypes.string,
    onClose: PropTypes.func,
    initialFocusRef: PropTypes.object,
    setfeatureImage: PropTypes.string,
    selectedLevelOneMenu: PropTypes.func,
    selectedLevelTwoMenu: PropTypes.func,
    setSelectedLevelTwoMenu: PropTypes.func,
    setLinksForLevelThreeMenu: PropTypes.func,
    setLinksForLevelTwoMenu: PropTypes.array,
    setIsLevelThreeMenuOpen: PropTypes.func,
    setIsLevelTwoMenuOpen: PropTypes.func,
    setLinksForContentComp: PropTypes.func,
    isMobileMenu: PropTypes.oneOf[(true, false)],
    setFeatureImageMobileLevelTwo: PropTypes.func,
    setFeatureImageMobileLevelThree: PropTypes.func,
    setFeatureImageMobileLevelFour: PropTypes.func,
    setMenuToggle: PropTypes.func,
    isLevelTwoMenuOpen: PropTypes.bool,
    isLevelThreeMenuOpen: PropTypes.bool,
    setSelectedLevelOneMenu: PropTypes.func
}

export {ListMenuContent}
